import { z } from 'zod'
import { FIELD_CANNOT_BE_EMPTY } from '../../../../../consts/validationErrors'
import { parseTagsInput } from '../EditDescriptionForm/helpers/tags'
import { parseExternalLinksInput } from '../EditDescriptionForm/helpers/externalLinks'
import { EXTERNAL_LINKS_MAX } from '../../../../../consts/projectExternalLinks'
import { ProjectStageEnum, ProjectStatusEnum, ProjectTypeEnum } from '../../../../../consts/projectTypeOptions'

export const descriptionSchema = z
  .object({
    name: z
      .string({
        required_error: FIELD_CANNOT_BE_EMPTY,
      })
      .trim()
      .min(1, { message: FIELD_CANNOT_BE_EMPTY })
      .min(2, { message: "Минимально 2 символа" })
      .max(500, { message: "Максимум 500 символов" }),
    type: z.nativeEnum(ProjectTypeEnum, { required_error: FIELD_CANNOT_BE_EMPTY }),
    stage: z.nativeEnum(ProjectStageEnum, { required_error: FIELD_CANNOT_BE_EMPTY }),
    region: z
      .string({ required_error: FIELD_CANNOT_BE_EMPTY })
      .trim()
      .min(1, { message: FIELD_CANNOT_BE_EMPTY }),
    tags: z
      .string()
      .optional()
      .superRefine((val, ctx) => {
        const input = val?.trim() ?? ""
        if (!input) return

        const tags = parseTagsInput(input)

        if (tags.length > 10) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Максимум 10 тегов",
          })
        }

        tags.forEach((tag) => {
          const withoutHash = tag.startsWith("#") ? tag.slice(1) : tag
          if (withoutHash.length < 1 || withoutHash.length > 18) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Длина "${tag}" должна быть от 1 до 18 символов`,
            })
          }
        })
      }),
    description: z
      .string({
        required_error: FIELD_CANNOT_BE_EMPTY,
      })
      .trim()
      .min(1, { message: FIELD_CANNOT_BE_EMPTY })
      .min(2, { message: "Минимально 2 символа" })
      .max(65535, { message: "Текст слишком длинный" }),
    full_description: z
      .string()
      .trim()
      .max(65535, { message: "Текст слишком длинный" })
      .default(''),
    env_factor: z
      .string({
        required_error: FIELD_CANNOT_BE_EMPTY,
      })
      .trim()
      .min(1, { message: FIELD_CANNOT_BE_EMPTY })
      .min(2, { message: "Минимально 2 символа" })
      .max(65535, { message: "Текст слишком длинный" }),
    expected_result: z
      .string({
        required_error: FIELD_CANNOT_BE_EMPTY,
      })
      .trim()
      .min(1, { message: FIELD_CANNOT_BE_EMPTY })
      .min(2, { message: "Минимально 2 символа" })
      .max(65535, { message: "Текст слишком длинный" }),
    initial_state: z
      .string({
        required_error: FIELD_CANNOT_BE_EMPTY,
      })
      .trim()
      .min(1, { message: FIELD_CANNOT_BE_EMPTY })
      .min(2, { message: "Минимально 2 символа" })
      .max(65535, { message: "Текст слишком длинный" }),
    necessary_actions: z
      .string({
        required_error: FIELD_CANNOT_BE_EMPTY,
      })
      .trim()
      .min(1, { message: FIELD_CANNOT_BE_EMPTY })
      .min(2, { message: "Минимально 2 символа" })
      .max(65535, { message: "Текст слишком длинный" }),
    implementation: z
      .string({
        required_error: FIELD_CANNOT_BE_EMPTY,
      })
      .trim()
      .min(1, { message: FIELD_CANNOT_BE_EMPTY })
      .min(2, { message: "Минимально 2 символа" })
      .max(65535, { message: "Текст слишком длинный" }),
    city: z.union([
      z.literal(''),
      z
        .string()
        .trim()
        .min(2, { message: "Минимально 2 символа" })
        .max(240, { message: "Максимально 240 символа" }),
    ]),
    address: z.union([
      z.literal(''),
      z
        .string()
        .trim()
        .min(2, { message: "Минимально 2 символа" })
        .max(240, { message: "Максимально 240 символа" }),
    ]),
    website: z.union([
      z.literal(''),
      z
        .string()
        .trim()
        .min(2, { message: 'Минимально 2 символа' })
        .max(65535, { message: 'Текст слишком длинный' })
        .url({ message: 'Некорректный URL' }),
    ]),
    external_links: z
      .string()
      .optional()
      .superRefine((val, ctx) => {
        const input = val?.trim() ?? ''
        if (!input) return

        const links = parseExternalLinksInput(input)

        if (links.length > EXTERNAL_LINKS_MAX) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Максимум ${EXTERNAL_LINKS_MAX} ссылок`,
          })
        }

        links.forEach((link, index) => {
          if (link.length > 2048) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Ссылка #${index + 1}: слишком длинный URL`,
            })
            return
          }
          const parsed = z.string().url().safeParse(link)
          if (!parsed.success) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Ссылка #${index + 1}: некорректный URL`,
            })
          }
        })
      }),
    status: z.nativeEnum(ProjectStatusEnum, { required_error: FIELD_CANNOT_BE_EMPTY }),
  })

export type ProjectDescriptionFormInput = z.input<typeof descriptionSchema>

export type ProjectDescriptionFormOutput = z.output<typeof descriptionSchema>